// Cloudflare Turnstile — explicit render mode via plain <script> tag.
// No npm dependency; the widget itself is loaded lazily and only once.
//
// Contract:
//   <Turnstile
//     siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
//     onToken={(token) => setToken(token)}   // fired on success
//     onExpire={() => setToken("")}          // fired when token expires
//     onError={() => setToken("")}           // network / config / reused
//     resetKey={someNumber}                  // bump to force a reset
//     testId="signup-turnstile"
//   />
//
// If `siteKey` is empty (dev/preview without Turnstile configured), the
// component renders an inline notice and immediately calls `onToken("")`
// so the caller can still submit — the backend fails-open when its
// TURNSTILE_SECRET_KEY is also absent, matching the environment.

import { useEffect, useRef } from "react";

const SCRIPT_ID  = "cf-turnstile-loader";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let _scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load",  () => resolve(window.turnstile));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload  = () => resolve(window.turnstile);
    s.onerror = () => reject(new Error("turnstile script failed"));
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

export default function Turnstile({
  siteKey,
  onToken,
  onExpire,
  onError,
  resetKey = 0,
  testId = "turnstile-widget",
  theme = "light",
}) {
  const hostRef   = useRef(null);
  const widgetRef = useRef(null);

  // Bypass mode — no siteKey configured (dev/preview). Notify the caller.
  useEffect(() => {
    if (!siteKey) { onToken && onToken(""); }
  }, [siteKey, onToken]);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    let ts;

    (async () => {
      try {
        ts = await loadScript();
      } catch {
        onError && onError("script");
        return;
      }
      if (cancelled || !hostRef.current) return;

      // If we already rendered a widget on this host, reset instead of re-render.
      if (widgetRef.current !== null) {
        try { ts.reset(widgetRef.current); } catch { /* stale */ }
        return;
      }
      try {
        widgetRef.current = ts.render(hostRef.current, {
          sitekey: siteKey,
          theme,
          language: "ar",
          callback: (token) => onToken && onToken(token || ""),
          "expired-callback": () => onExpire && onExpire(),
          "error-callback":   () => onError  && onError("widget"),
          "timeout-callback": () => onExpire && onExpire(),
        });
      } catch {
        onError && onError("render");
      }
    })();

    return () => {
      cancelled = true;
      if (widgetRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetRef.current); } catch { /* no-op */ }
        widgetRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  // Force a widget reset when the parent bumps `resetKey` (after a
  // failed submission whose CAPTCHA was consumed).
  useEffect(() => {
    if (!siteKey || widgetRef.current === null || !window.turnstile) return;
    try { window.turnstile.reset(widgetRef.current); } catch { /* no-op */ }
    onToken && onToken("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!siteKey) {
    return (
      <div
        data-testid={`${testId}-bypass`}
        role="status"
        className="rounded-md border border-dashed border-surface-300 bg-surface-50 px-3 py-2 text-[11px] leading-relaxed text-ink-500"
      >
        التحقّق من الروبوتات غير مُفعّل في هذه البيئة. سيُطلب في الإنتاج.
      </div>
    );
  }

  return (
    <div className="flex justify-center" data-testid={testId}>
      <div ref={hostRef} dir="ltr" />
    </div>
  );
}
